import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { getGradientClasses } from '../../utils/helpers';
import { SparklesIcon } from 'lucide-react';
interface StartupAnimationProps {
  onComplete: () => void;
}
export function StartupAnimation({
  onComplete
}: StartupAnimationProps) {
  const {
    getThemeClasses,
    theme
  } = useTheme();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme);
  const [step, setStep] = useState(0);
  const steps = ['Creativiteit initialiseren...', 'Design-systemen laden...', 'AI-engine activeren...', 'Portfolio voorbereiden...', 'MVL Experience gereed'];
  useEffect(() => {
    const timers = [setTimeout(() => setStep(1), 800), setTimeout(() => setStep(2), 1600), setTimeout(() => setStep(3), 2400), setTimeout(() => setStep(4), 3200), setTimeout(() => onComplete(), 4200)];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3
  }} className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-id="element-840">
      <div className="text-center" data-id="element-841">
        {/* Logo with enhanced animated elements */}
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="relative w-28 h-28 mx-auto mb-10" data-id="element-842">
          {/* Outer glow ring */}
          <motion.div className="absolute inset-0 rounded-3xl blur-2xl opacity-40" animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.6, 0.4]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} style={{
          background: `linear-gradient(135deg, ${theme === 'neon' ? '#06b6d4, #ec4899' : '#a855f7, #ec4899'})`
        }} data-id="element-843" />
          {/* Main rotating square */}
          <motion.div className={`absolute inset-0 w-24 h-24 mx-auto my-auto rounded-2xl bg-gradient-to-br ${gradients.primary} flex items-center justify-center shadow-2xl ${theme === 'neon' ? 'shadow-cyan-500/50' : 'shadow-purple-500/50'}`} animate={{
          rotate: [0, 360]
        }} transition={{
          rotate: {
            duration: 20,
            ease: 'linear',
            repeat: Infinity
          }
        }} data-id="element-844" />
          {/* Inner content */}
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-845">
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.4
          }} className="text-center z-10" data-id="element-846">
              <span className="text-4xl font-bold text-white block" data-id="element-847">MVL</span>
              <span className="text-xs font-semibold text-white/80 tracking-wider" data-id="element-848">
                CREATIVE
              </span>
            </motion.div>
          </div>
          {/* Animated sparkles */}
          <motion.div className="absolute inset-0" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} data-id="element-849">
            <motion.div animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-850">
              <SparklesIcon className="absolute -top-3 -right-3 w-7 h-7 text-white" data-id="element-851" />
            </motion.div>
            <motion.div animate={{
            scale: [1, 1.2, 1],
            rotate: [360, 180, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} data-id="element-852">
              <SparklesIcon className="absolute -bottom-3 -left-3 w-7 h-7 text-white" data-id="element-853" />
            </motion.div>
          </motion.div>
        </motion.div>
        {/* System name with enhanced animation */}
        <motion.h1 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.5
      }} className="text-3xl font-bold text-white mb-3" data-id="element-854">
          Mick van Latum Creative
        </motion.h1>
        {/* Loading steps with better transitions */}
        <div className="h-8 mb-8" data-id="element-855">
          <AnimatePresence mode="wait" data-id="element-856">
            <motion.p key={step} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.4
          }} className="text-white/70 text-base font-mono" data-id="element-857">
              {steps[step]}
            </motion.p>
          </AnimatePresence>
        </div>
        {/* Enhanced progress bar */}
        <div className="w-64 h-2.5 mx-auto bg-white/10 rounded-full overflow-hidden shadow-inner" data-id="element-858">
          <motion.div initial={{
          width: '0%'
        }} animate={{
          width: '100%'
        }} transition={{
          duration: 4,
          ease: 'easeInOut'
        }} className="h-full relative" data-id="element-859">
            <div className={`absolute inset-0 bg-gradient-to-r ${gradients.primary}`} data-id="element-860"></div>
            <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent" animate={{
            x: ['-100%', '200%']
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-861" />
          </motion.div>
        </div>
        {/* Version info with fade in */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} className="text-white/40 text-sm font-mono mt-6" data-id="element-862">
          2024 • Design × AI × Development
        </motion.p>
        {/* Subtle loading dots */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="flex items-center justify-center gap-2 mt-4" data-id="element-863">
          {[0, 1, 2].map(i => <motion.div key={i} className="w-2 h-2 rounded-full bg-white/30" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.3, 0.8, 0.3]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          delay: i * 0.2
        }} data-id="element-864" />)}
        </motion.div>
      </div>
    </motion.div>;
}